// Rules for VALIDATION_CONSTRAINTS.md section 12 — Diagram / Process level.
//
// Skipped rules:
//   - 12.1 (Diagram.Version == 1): already enforced by pssHelper which
//     hardcodes the version to 1; validation at this layer cannot see the
//     generated diagram's Version field.
//   - 12.5 (Extension shape): the doc claims Extensions must be a single
//     JToken. This is OUTDATED — per docs/engine_schema.json the Peak engine
//     accepts Extensions as an ARRAY. No validation emitted.
//   - 12.8 (Unused DiagramResource entries): gated on resources persisting;
//     see section 11. Emitted as a warning placeholder only when resources
//     are present.
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';

// 12.1 skipped — enforced by pssHelper hardcoding Version=1.

const rule_12_2_DiagramHasProcess: ValidationRule = {
  id: '12.2',
  description: 'Diagram must contain at least one process',
  scope: 'diagram',
  run: (ctx) => {
    if (ctx.processes.length === 0) {
      return [
        {
          severity: 'error',
          rule: '12.2',
          message: 'Diagram must contain at least one process.',
        },
      ];
    }
    return [];
  },
};
registerRule(rule_12_2_DiagramHasProcess);

const rule_12_3_ProcessNameNonEmpty: ValidationRule = {
  id: '12.3',
  description: 'Process.name must be non-empty',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const name = process.name;
    if (!name || name.trim() === '') {
      return [
        {
          severity: 'error',
          rule: '12.3',
          message: `Process '${process.id}' has an empty name.`,
          processId: process.id,
        },
      ];
    }
    return [];
  },
};
registerRule(rule_12_3_ProcessNameNonEmpty);

const rule_12_4_ProcessNameUnique: ValidationRule = {
  id: '12.4',
  description: 'Process names must be unique within the diagram',
  scope: 'diagram',
  run: (ctx) => {
    const issues: ValidationIssue[] = [];
    const byName = new Map<string, number>();
    for (const p of ctx.processes) {
      const name = (p.name ?? '').trim();
      if (!name) continue; // Empty names are handled by 12.3.
      byName.set(name, (byName.get(name) ?? 0) + 1);
    }
    const dupes = Array.from(byName.entries())
      .filter(([, count]) => count > 1)
      .map(([name]) => name);
    if (dupes.length > 0) {
      issues.push({
        severity: 'error',
        rule: '12.4',
        message: `Duplicate process names within diagram: ${dupes.join(', ')}.`,
      });
    }
    return issues;
  },
};
registerRule(rule_12_4_ProcessNameUnique);

// 12.5 skipped — doc is outdated; engine_schema.json accepts Extensions as an array.

const rule_12_6_NodeSingleProcess: ValidationRule = {
  id: '12.6',
  description: 'Every node must be assigned to exactly one process',
  scope: 'diagram',
  run: (ctx) => {
    const issues: ValidationIssue[] = [];
    // node id -> list of process names containing it.
    const nodeProcesses = new Map<string, string[]>();
    for (const p of ctx.processes) {
      const label = p.name || p.id;
      for (const n of p.nodes) {
        const list = nodeProcesses.get(n.id) ?? [];
        list.push(label);
        nodeProcesses.set(n.id, list);
      }
    }
    for (const [nodeId, procs] of nodeProcesses) {
      if (procs.length > 1) {
        issues.push({
          severity: 'error',
          rule: '12.6',
          message: `Node '${nodeId}' appears in multiple processes: ${procs.join(', ')}.`,
          nodeIds: [nodeId],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_12_6_NodeSingleProcess);

const rule_12_7_OrphanSubProcess: ValidationRule = {
  id: '12.7',
  description:
    'Sub-process definitions should be referenced by at least one CallActivity',
  scope: 'diagram',
  run: (ctx) => {
    const issues: ValidationIssue[] = [];
    // Collect every processName referenced by CallActivity nodes across all
    // processes.
    const referenced = new Set<string>();
    for (const p of ctx.processes) {
      for (const n of p.nodes) {
        if (n.type !== 'CallActivity') continue;
        const name = (n.data as any)?.processName;
        if (typeof name === 'string' && name.trim() !== '') {
          referenced.add(name.trim());
        }
      }
    }
    for (const p of ctx.processes) {
      if (!p.isSubProcess) continue;
      const name = (p.name ?? '').trim();
      if (!name) continue;
      if (!referenced.has(name)) {
        issues.push({
          severity: 'warning',
          rule: '12.7',
          message: `Sub-process '${name}' is not referenced by any CallActivity.`,
          processId: p.id,
        });
      }
    }
    return issues;
  },
};
registerRule(rule_12_7_OrphanSubProcess);

const rule_12_8_UnusedResources: ValidationRule = {
  id: '12.8',
  description:
    'DiagramResource entries unused by any node (gated on resources persisting)',
  scope: 'diagram',
  run: (ctx) => {
    const issues: ValidationIssue[] = [];
    // Gather all persisted resource keys — currently not produced by the editor.
    const allKeys = new Set<string>();
    for (const p of ctx.processes) {
      const resources = (p as any).resources;
      if (!resources || typeof resources !== 'object') continue;
      for (const key of Object.keys(resources)) allKeys.add(key);
    }
    if (allKeys.size === 0) return issues;

    const used = new Set<string>();
    for (const p of ctx.processes) {
      for (const n of p.nodes) {
        const d = n.data as any;
        if (!d) continue;
        for (const field of ['inputScripts', 'outputScripts', 'exportScripts']) {
          const value = d[field];
          if (!Array.isArray(value)) continue;
          for (const id of value) used.add(String(id));
        }
      }
    }
    for (const key of allKeys) {
      if (!used.has(key)) {
        issues.push({
          severity: 'warning',
          rule: '12.8',
          message: `Resource '${key}' is defined but never referenced.`,
        });
      }
    }
    return issues;
  },
};
registerRule(rule_12_8_UnusedResources);
